"use client";

import { TrendingUp, Users, Clock, Award, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Phone, Mail, UserMinus, Eye, Search, MapPin } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { useAgentLeads, AGENT_LEADS_PAGE_SIZE } from "../hooks/queries";
import { useAgentStatusBreakdown } from "../hooks/useAgentStatusBreakdown";
import { useUnassignLead } from "../hooks/useUnassignLead";
import { LeadDetailsPanel } from "@/app/(authenticated)/leads/_modules/components/LeadDetailsPanel";

interface Agent {
  agentId: string;
  agentName: string;
  email: string;
  agencyName?: string; // Agency name for identifying sub-agency members
  activeLeadsCount: number;
  maxLeads: number;
  // Mirrors UserStatusSelector statuses. Legacy values kept for backward compat.
  // Note: AgentPerformance doesn't render this field visually — kept in type only.
  status: "available" | "idle" | "on_call" | "dnd" | "offline" | "online" | "active" | "busy" | "away";
  lastActivity: string | null;
  conversionRate: number;
  avgResponseTime: string;
}

interface AgentPerformanceProps {
  agents: Agent[];
  isLoading?: boolean;
}

export const AgentPerformance = function ({ agents, isLoading = false }: AgentPerformanceProps) {
  const [expandedAgentId, setExpandedAgentId] = useState<string | null>(null);
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);
  const [agentLeadsPage, setAgentLeadsPage] = useState(1);
  const [agentLeadsTotal, setAgentLeadsTotal] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const queryClient = useQueryClient();
  const router = useRouter();
  // Per-agent stage counts (system + custom pipelines) for the chip breakdown
  const { data: statusBreakdown } = useAgentStatusBreakdown();
  const breakdownByAgent = statusBreakdown?.agents || {};
  const unassignMutation = useUnassignLead();

  // Lazy + paginated: fetch ONE page (50) of the expanded agent's leads, server-side
  // filtered to that agent. Replaces the old "fetch 500 agency-wide + filter client-side"
  // (which missed older leads) — now it shows every lead via paging and never loads
  // thousands at once.
  const { data: agentLeadsData, isLoading: isLoadingLeads, isFetching: isFetchingLeads } =
    useAgentLeads(expandedAgentId ?? undefined, agentLeadsPage, { enabled: !!expandedAgentId });
  const expandedAgentLeads = agentLeadsData?.leads ?? [];
  const hasNextLeadsPage = expandedAgentLeads.length === AGENT_LEADS_PAGE_SIZE;
  // Backend only returns the count on page 1 — preserve it for the pager label.
  useEffect(() => {
    if (typeof agentLeadsData?.totalItems === "number") setAgentLeadsTotal(agentLeadsData.totalItems);
  }, [agentLeadsData?.totalItems]);
  const agentLeadsTotalPages = agentLeadsTotal != null ? Math.max(1, Math.ceil(agentLeadsTotal / AGENT_LEADS_PAGE_SIZE)) : null;

  // Sort agents by conversion rate (search filters by name/email/agency first)
  const q = searchQuery.trim().toLowerCase();
  const sortedAgents = (q
    ? agents.filter(
        (a) =>
          (a.agentName || "").toLowerCase().includes(q) ||
          (a.email || "").toLowerCase().includes(q) ||
          (a.agencyName || "").toLowerCase().includes(q),
      )
    : [...agents]
  ).sort((a, b) => b.conversionRate - a.conversionRate);

  const handleAgentClick = (agentId: string) => {
    setExpandedAgentId(expandedAgentId === agentId ? null : agentId);
    setAgentLeadsPage(1); // reset paging whenever expanding/collapsing
    setAgentLeadsTotal(null);
    setExpandedLeadId(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatPhone = (phone: string) => {
    if (!phone) return 'N/A';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // Format as (xxx) xxx-xxxx
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits[0] === '1') {
      // Handle +1 prefix
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length > 10) {
      // Handle any extra digits by taking last 10
      const last10 = digits.slice(-10);
      return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
    }

    // Return N/A if not enough digits
    return 'N/A';
  };

  const handleUnassign = (leadId: string, leadStatus: string, agentId: string) => {
    unassignMutation.mutate({
      leadIds: [leadId],
      context: {
        leadStatuses: [leadStatus || 'New lead'],
        agentId,
      },
    });
  };

  // Normalize status display name (New Lead -> Leads)
  const getStatusDisplayName = (status: string): string => {
    const normalized = status?.toLowerCase();
    if (normalized === 'new lead' || normalized === 'new') {
      return 'Leads';
    }
    return status || 'Leads';
  };

  const getStatusColor = (status: string) => {
    const normalized = status?.toLowerCase();
    switch (normalized) {
      case 'sold & approved':
      case 'sold':
      case 'approved':
        return 'bg-emerald-600 text-emerald-100';
      case 'appt':
      case 'appointment':
      case 'scheduled':
        return 'bg-amber-500 text-amber-100';
      case 'underwriting':
      case 'under writing':
        return 'bg-cyan-600 text-cyan-100';
      case 'new lead':
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'bad number':
      case 'bad #':
        return 'bg-red-500 text-red-100';
      case 'no sale':
        return 'bg-red-600 text-red-100';
      case 'do not call':
        return 'bg-amber-800 text-amber-100';
      case 'disposed':
        return 'bg-gray-600 text-gray-100';
      default:
        return 'bg-gray-600 text-gray-100';
    }
  };

  const handleToggleLeadDetails = (leadId: string) => {
    setExpandedLeadId(expandedLeadId === leadId ? null : leadId);
  };

  const handleLeadPanelClose = () => {
    setExpandedLeadId(null);
  };

  const handleLeadSave = async () => {
    // Refresh data after lead is saved
    await queryClient.invalidateQueries({ queryKey: ['allLeads'] });
    await queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  // Show loading state when agents are loading
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Agent Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-500 text-sm">Loading agent performance data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show empty state when no agents
  if (!agents || agents.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Agent Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Users className="h-12 w-12 mb-4 text-gray-300" />
            <p className="text-sm">No agents found in your agency</p>
            <p className="text-xs mt-1">Add agents to see their performance metrics</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col max-h-[calc(100vh-220px)]">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Agent Performance</CardTitle>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search agents..."
              className="h-8 w-[180px] pl-8 text-xs"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          {sortedAgents.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <Users className="h-10 w-10 mb-2 text-gray-300" />
              <p className="text-sm">No agents match &quot;{searchQuery}&quot;</p>
            </div>
          )}
          {sortedAgents.map((agent, index) => {
            const isExpanded = expandedAgentId === agent.agentId;
            const agentLeads = isExpanded ? expandedAgentLeads : [];

            return (
              <div key={agent.agentId} className="border border-gray-200 rounded-lg">
                <div
                  onClick={() => handleAgentClick(agent.agentId)}
                  className={`flex items-center justify-between p-4 transition-colors cursor-pointer ${
                    isExpanded
                      ? "bg-primary/5 hover:bg-primary/10"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full font-semibold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {agent.agentName}
                        {agent.agencyName && (
                          <span className="text-primary font-normal"> - {agent.agencyName}</span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-600">{agent.email}</p>
                      {(() => {
                        const stages = breakdownByAgent[agent.agentId];
                        if (!stages) return null;
                        const entries = Object.entries(stages)
                          .filter(([, count]) => count > 0)
                          .sort((a, b) => b[1] - a[1]);
                        if (entries.length === 0) return null;
                        return (
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {entries.map(([stage, count]) => (
                              <span
                                key={stage}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full ${getStatusColor(stage)}`}
                              >
                                {getStatusDisplayName(stage)}
                                <span className="font-semibold">{count.toLocaleString()}</span>
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-green-600">
                        <TrendingUp className="h-4 w-4" />
                        <span className="font-semibold">{agent.conversionRate}%</span>
                      </div>
                      <div className="text-xs text-gray-500">Conversion</div>
                    </div>

                    <div className="text-center">
                      <div className="flex items-center gap-1 text-primary">
                        <Users className="h-4 w-4" />
                        <span className="font-semibold">{agent.activeLeadsCount}</span>
                      </div>
                      <div className="text-xs text-gray-500">Active Leads</div>
                    </div>

                    <div className="text-center">
                      <div className="flex items-center gap-1 text-purple-600">
                        <Clock className="h-4 w-4" />
                        <span className="font-semibold">{agent.avgResponseTime}</span>
                      </div>
                      <div className="text-xs text-gray-500">Total Call Time</div>
                    </div>

                    {index === 0 && (
                      <div className="flex items-center gap-1 text-yellow-600">
                        <Award className="h-4 w-4" />
                        <span className="text-xs font-medium">Top Performer</span>
                      </div>
                    )}

                    <div className="ml-2">
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded leads table */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-white">
                    {isLoadingLeads ? (
                      <div className="p-8 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                        <p>Loading leads...</p>
                      </div>
                    ) : agentLeads.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No leads assigned to this agent</p>
                      </div>
                    ) : (
                      <div>
                        <div className="px-4 py-2 bg-gray-50 border-b text-sm text-gray-600 flex items-center justify-between gap-2">
                          <span>
                            {agentLeadsTotal != null
                              ? `${agentLeadsTotal.toLocaleString()} lead${agentLeadsTotal !== 1 ? 's' : ''} · page ${agentLeadsPage}${agentLeadsTotalPages ? ` of ${agentLeadsTotalPages}` : ''}`
                              : `Page ${agentLeadsPage} · showing ${agentLeads.length} lead${agentLeads.length !== 1 ? 's' : ''}`}
                            {isFetchingLeads ? ' …' : ''}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline" size="sm" className="h-7 px-2"
                              disabled={agentLeadsPage === 1 || isFetchingLeads}
                              onClick={(e) => { e.stopPropagation(); setAgentLeadsPage((p) => Math.max(1, p - 1)); }}
                            >
                              <ChevronLeft className="h-4 w-4" /> Prev
                            </Button>
                            <Button
                              variant="outline" size="sm" className="h-7 px-2"
                              disabled={isFetchingLeads || (agentLeadsTotalPages != null ? agentLeadsPage >= agentLeadsTotalPages : !hasNextLeadsPage)}
                              onClick={(e) => { e.stopPropagation(); setAgentLeadsPage((p) => p + 1); }}
                            >
                              Next <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className={expandedLeadId ? "overflow-y-auto" : "max-h-[400px] overflow-y-auto"}>
                          <Table>
                            <TableHeader className="sticky top-0 bg-white z-10 shadow-sm">
                              <TableRow>
                                <TableHead>Lead Name</TableHead>
                                <TableHead>Contact</TableHead>
                                <TableHead>State</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Last Call</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                          <TableBody>
                            {agentLeads.map((lead: any) => {
                              const isLeadExpanded = expandedLeadId === lead.id;
                              return (
                                <React.Fragment key={lead.id}>
                                  <TableRow
                                    className={`cursor-pointer transition-colors ${
                                      isLeadExpanded
                                        ? "bg-primary/10 hover:bg-primary/10"
                                        : "hover:bg-primary/5"
                                    }`}
                                    onClick={() => handleToggleLeadDetails(lead.id)}
                                  >
                                    <TableCell className="font-medium text-gray-900 hover:text-gray-700">
                                      <div className="flex items-center gap-2">
                                        {isLeadExpanded ? (
                                          <ChevronUp className="h-4 w-4 text-primary" />
                                        ) : (
                                          <ChevronDown className="h-4 w-4 text-gray-400" />
                                        )}
                                        {lead.name}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex flex-col gap-1 text-sm">
                                        {lead.phone && (
                                          <div className="flex items-center gap-1 text-gray-600">
                                            <Phone className="h-3 w-3" />
                                            {formatPhone(lead.phone)}
                                          </div>
                                        )}
                                        {lead.email && (
                                          <div className="flex items-center gap-1 text-gray-600">
                                            <Mail className="h-3 w-3" />
                                            {lead.email}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-700">{lead.state || 'N/A'}</TableCell>
                                    <TableCell>
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(lead.leadStat || 'new')}`}>
                                        {getStatusDisplayName(lead.leadStat)}
                                      </span>
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-600">
                                      {formatDate(lead.lastCall)}
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Shell: open the NATIVE lead screen — the inline
                                            // desktop panel is unusable inside a phone-width table.
                                            if (window.location.pathname.startsWith("/m")) {
                                              router.push(`/m/leads/${lead.id}`);
                                              return;
                                            }
                                            handleToggleLeadDetails(lead.id);
                                          }}
                                          className={`${
                                            isLeadExpanded
                                              ? "text-primary bg-primary/20 hover:bg-primary/30"
                                              : "text-primary hover:text-primary/80 hover:bg-primary/5"
                                          }`}
                                        >
                                          <Eye className="h-4 w-4 mr-1" />
                                          {isLeadExpanded ? 'Hide' : 'View'}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnassign(lead.id, lead.leadStat || 'New lead', agent.agentId);
                                          }}
                                          disabled={unassignMutation.isPending}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <UserMinus className="h-4 w-4 mr-1" />
                                          {unassignMutation.isPending ? 'Unassigning...' : 'Unassign'}
                                        </Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                  {/* Inline Lead Details Panel.
                                      Shell: the desktop LeadDetailsLayout is a two-column
                                      monster that can never fit a phone-width table cell —
                                      render a NATIVE mini-panel instead (key facts + Open
                                      Full Lead → /m/leads/[id]). */}
                                  {isLeadExpanded && (
                                    <TableRow>
                                      <TableCell colSpan={6} className="p-0 bg-gray-50">
                                        {typeof window !== "undefined" && window.location.pathname.startsWith("/m") ? (
                                          <div
                                            className="space-y-2.5 border-l-[3px] border-primary bg-white p-3"
                                            style={{ position: "sticky", left: 0, maxWidth: "calc(100vw - 62px)" }}
                                          >
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="min-w-0">
                                                <p className="truncate text-[15px] font-semibold text-gray-900">{lead.name}</p>
                                                {lead.phone && (
                                                  <p className="text-xs text-gray-500">{formatPhone(lead.phone)}</p>
                                                )}
                                              </div>
                                              <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${getStatusColor(lead.leadStat || 'new')}`}>
                                                {lead.leadStat || 'New lead'}
                                              </span>
                                            </div>
                                            <div className="space-y-1 text-[12.5px] text-gray-600">
                                              {lead.email && (
                                                <p className="flex min-w-0 items-center gap-1.5">
                                                  <Mail className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                  <span className="truncate">{lead.email}</span>
                                                </p>
                                              )}
                                              {lead.state && (
                                                <p className="flex items-center gap-1.5">
                                                  <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                  {lead.state}
                                                </p>
                                              )}
                                              <p className="flex items-center gap-1.5">
                                                <Phone className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                                                Last call: {lead.lastCall ? new Date(lead.lastCall).toLocaleDateString() : 'never'}
                                              </p>
                                            </div>
                                            <Button
                                              size="sm"
                                              className="w-full"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                router.push(`/m/leads/${lead.id}`);
                                              }}
                                            >
                                              <Eye className="mr-1.5 h-3.5 w-3.5" />
                                              Open Full Lead
                                            </Button>
                                          </div>
                                        ) : (
                                          <div className="p-4 border-l-4 border-primary">
                                            <LeadDetailsPanel
                                              leadId={lead.id}
                                              onClose={handleLeadPanelClose}
                                              compact={true}
                                              showCloseButton={true}
                                              onSave={handleLeadSave}
                                              alwaysEditing={false}
                                            />
                                          </div>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
